import { ModelType } from '@/modules/core/app/constants/ModelType';
import { capitalize } from '@/modules/core/app/helpers/StringHelper';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import Lang from '@/modules/core/app/services/Lang';
import { Document } from '@/modules/document/models/Document';
import { Estate } from '@/modules/estate/models/Estate';
import { EstateTaskEditorTemplateDocumentStatus } from '@/modules/estatetask/enums/EstateTaskEditorTemplateDocumentStatus';
import { EstateTaskEditorTemplateDocumentTypeKey } from '@/modules/estatetask/enums/EstateTaskEditorTemplateDocumentTypeKey';
import { EstateTask } from '@/modules/estatetask/models/EstateTask';
import { EditorTemplate } from '@/modules/export/_modules/editor/models/EditorTemplate';
import { Note } from '@/modules/note/models/Note';
import { NotificationHistory } from '@/modules/notification/models/NotificationHistory';
import { User } from '@/modules/user/models/User';
import { Will } from '@/modules/will/models/Will';

export class EditorTemplateDocument extends BaseEntityModel {
  id?: number;
  estateId?: number;
  editorTemplateId?: number;
  documentId?: number;
  modelId?: number;
  modelType?: string;
  changesRequestedExplanation?: string;
  editorTemplate: Relationship<EditorTemplate>;
  document?: Relationship<Document | null>;
  notificationHistories?: Relationship<NotificationHistory[] | null>;
  estate?: Relationship<Estate | null>;
  estateTask?: Relationship<EstateTask[] | null>;
  will?: Relationship<Will | null>;
  notes?: Relationship<Note[] | null>;

  constructor(model: Partial<EditorTemplateDocument> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.editorTemplateId = model.editorTemplateId;
    this.documentId = model.documentId;
    this.modelId = model.modelId;
    this.modelType = model.modelType;
    this.changesRequestedExplanation = model.changesRequestedExplanation;
    this.editorTemplate = EditorTemplate.mapModel(model.editorTemplate);
    this.document = Document.mapModelOrNull(model.document);
    this.notificationHistories = NotificationHistory.mapModelsOrNull(model.notificationHistories);
    this.estate = Estate.mapModelOrNull(model.estate);
    this.estateTask = EstateTask.mapModelsOrNull(model.estateTask);
    this.will = Will.mapModelOrNull(model.will);
    this.notes = Note.mapModelsOrNull(model.notes);
  }

  get formattedLastEditedAt(): string {
    if (!this.editorTemplate.lastEditedContentsAuditByUser) {
      return Lang.t('document.working-file');
    }

    const audit: typeof this.editorTemplate.lastEditedContentsAuditByUser = this.editorTemplate.lastEditedContentsAuditByUser;
    const subtitle: string = audit.user?.name
      ? Lang.t('document.last-edit-subtitle', { date: audit.formattedCreatedAt, user: audit.user.name })
      : Lang.t('document.last-edit-subtitle-no-user', { date: audit.formattedCreatedAt });

    return `${subtitle} - ${Lang.t('document.working-file')}`;
  }

  get qaReviewerEstateTask(): EstateTask | undefined {
    if (!this.estateTask) {
      return undefined;
    }

    return this.estateTask.find(
      (task: EstateTask) => task.type === EstateTaskEditorTemplateDocumentTypeKey.QA_REVIEWER
    );
  }

  get isChangesRequested(): boolean {
    return !!this.estateTask?.some(
      (task: EstateTask) => task.status === EstateTaskEditorTemplateDocumentStatus.CHANGES_REQUESTED
    );
  }

  get isWillModelType(): boolean {
    return this.modelType === ModelType.WILL;
  }

  get name(): string {
    return this.editorTemplate?.name ?? '-';
  }

  get draftName(): string {
    const name: string = this.editorTemplate?.name || '-';

    if (name === '-') {
      return name;
    }

    const parts: string[] = name.split(' - ');

    if (parts.length === 1) {
      return `${parts[0]} - ${Lang.t('will.document-draft')}`;
    }

    return `${parts[0]} - ${Lang.t('will.document-draft')} - ${parts.slice(1).join(' - ')}`;
  }

  get draftType(): string {
    return `${capitalize(this.editorTemplate?.type ?? '')} - ${Lang.t('will.document-draft')}`;
  }

  get hasDocument(): boolean {
    return !!this.document;
  }

  get createdByUser(): User | null {
    return this.editorTemplate?.createdAuditByUser?.user ?? null;
  }

  get lastEditedBy(): User | null {
    return this.editorTemplate?.lastEditedContentsAuditByUser?.user ?? null;
  }
}
